import { Clock, AlertCircle, CheckCircle, ListTodo, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

interface TodoOverviewCardsProps {
  total: number;
  overdue: number;
  completed: number;
  loading: boolean;
}

export const TodoOverviewCards = ({ total, overdue, completed, loading }: TodoOverviewCardsProps) => {
  const navigate = useNavigate();
  const pending = total - completed;
  const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className="grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x">
          {/* Total */}
          <div 
            className="p-4 cursor-pointer hover:bg-muted/30 transition-colors"
            onClick={() => navigate('/todos')}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 rounded-lg bg-muted">
                <ListTodo className="h-4 w-4 text-muted-foreground" />
              </div>
              <span className="text-2xl font-bold">{loading ? '...' : total}</span>
            </div>
            <p className="text-sm text-muted-foreground">Gesamt</p>
          </div>

          {/* Pending */}
          <div 
            className="p-4 cursor-pointer hover:bg-amber-50 transition-colors"
            onClick={() => navigate('/todos', { state: { filter: 'pending' } })}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 rounded-lg bg-amber-100">
                <Clock className="h-4 w-4 text-amber-600" />
              </div>
              <span className="text-2xl font-bold text-amber-600">{loading ? '...' : pending}</span>
            </div>
            <p className="text-sm text-muted-foreground">Offen</p>
          </div>

          {/* Overdue */}
          <div 
            className={cn(
              "p-4 cursor-pointer transition-colors",
              overdue > 0 ? "hover:bg-red-50" : "hover:bg-muted/30"
            )}
            onClick={() => navigate('/todos', { state: { filter: 'overdue' } })}
          >
            <div className="flex items-center justify-between mb-2">
              <div className={cn(
                "p-2 rounded-lg",
                overdue > 0 ? "bg-red-100" : "bg-muted"
              )}>
                <AlertCircle className={cn(
                  "h-4 w-4",
                  overdue > 0 ? "text-red-600" : "text-muted-foreground"
                )} />
              </div>
              <span className={cn(
                "text-2xl font-bold",
                overdue > 0 ? "text-red-600" : "text-muted-foreground"
              )}>
                {loading ? '...' : overdue}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">Überfällig</p>
          </div>

          {/* Completed with progress */}
          <div 
            className="p-4 cursor-pointer hover:bg-green-50 transition-colors"
            onClick={() => navigate('/todos', { state: { filter: 'completed' } })}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 rounded-lg bg-green-100">
                <CheckCircle className="h-4 w-4 text-green-600" />
              </div>
              <span className="text-2xl font-bold text-green-600">{loading ? '...' : completed}</span>
            </div>
            <div className="space-y-1">
              <Progress value={completionRate} className="h-1.5" />
              <p className="text-xs text-muted-foreground">{completionRate}% erledigt</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
